import { useCallback, useContext, useEffect, useState } from "react";
import { ApiContext } from "../../context/MyContext";
import { getBulkUserDetails } from "../../service/AuthWellDhanService";
import { fetchEmployees } from "../../service/employeeService";
import { commsIdentityCache } from "../../utils/commsIdentityCache";
import { createClientNameBatcher } from "./clientNameBatcher";

// Module-level so every component instance shares one cache, one coach
// bulk-fetch, and one client-name batcher.
let memoryCache = null; // { clients: { [clientId]: name }, coaches: { [userId]: name } }
let coachesInFlightPromise = null;
const subscribers = new Set(); // re-render callbacks for mounted useCommsIdentity() instances
// Coach ids we've already forced a re-fetch for on a cache miss — avoids
// retry storms for a coach who genuinely has no name set server-side.
const missingCoachRetried = new Set();

const notifySubscribers = () => subscribers.forEach((fn) => fn());

const buildName = (firstName, lastName) => {
  const name = `${firstName || ""} ${lastName || ""}`.trim();
  return name || undefined;
};

// Keyed by both user_id and employee_id: callers sometimes hand us a coach's
// employee_id instead of their user_id, and /fetch-employees already carries
// the name for both, so there's no need for a second per-coach lookup.
const mapCoaches = (employees) => {
  const map = {};
  (employees || []).forEach((employee) => {
    const name = buildName(employee?.first_name, employee?.last_name);
    if (!name) return;
    if (employee?.user_id != null) map[employee.user_id] = name;
    if (employee?.employee_id != null) map[employee.employee_id] = name;
  });
  return map;
};

const persist = () => commsIdentityCache.set(memoryCache);

// Coaches: fetched in bulk once per cache window via the proven fetchEmployees endpoint.
const refreshCoaches = (get) => {
  if (coachesInFlightPromise) return coachesInFlightPromise;

  coachesInFlightPromise = fetchEmployees(get)
    .then((employeesResult) => {
      memoryCache = {
        clients: memoryCache?.clients || {},
        coaches: mapCoaches(employeesResult?.employees),
      };
      persist();
      notifySubscribers();
    })
    .catch((err) => {
      console.error("useCommsIdentity: failed to fetch employees", err);
    })
    .finally(() => {
      coachesInFlightPromise = null;
    });

  return coachesInFlightPromise;
};

// Clients: resolved lazily via POST /users/bulk-details — ids requested
// during a render burst are coalesced into one bulk call (chunked at 100)
// by the shared batcher, then cached for the 1h cache window like before.
const clientNameBatcher = createClientNameBatcher({
  fetchBulk: getBulkUserDetails,
  hasName: (id) => !!memoryCache?.clients?.[id],
  buildName,
  onNames: (updates) => {
    memoryCache = {
      clients: { ...(memoryCache?.clients || {}), ...updates },
      coaches: memoryCache?.coaches || {},
    };
    persist();
    notifySubscribers();
  },
});

/**
 * Resolves client/coach display names from ids, backed by a shared
 * in-memory + localStorage cache (1h TTL). Coach names come from one bulk
 * fetch shared by every mounted component; client names are resolved lazily —
 * only ids actually rendered are requested, coalesced into bulk calls.
 */
export function useCommsIdentity() {
  const { get } = useContext(ApiContext);
  const [, forceRender] = useState(0);

  useEffect(() => {
    if (!memoryCache) {
      const cached = commsIdentityCache.get();
      if (cached) memoryCache = cached;
    }

    const rerender = () => forceRender((n) => n + 1);
    subscribers.add(rerender);

    if (!commsIdentityCache.isValid() || !memoryCache?.coaches) {
      refreshCoaches(get);
    }

    return () => subscribers.delete(rerender);
  }, [get]);

  const getClientName = useCallback((clientId) => {
    if (clientId == null) return undefined;
    const cached = memoryCache?.clients?.[clientId];
    if (!cached) clientNameBatcher.enqueue(clientId);
    return cached;
  }, []);

  const getCoachName = useCallback(
    (userId) => {
      if (userId == null) return undefined;
      const cached = memoryCache?.coaches?.[userId];
      // Bulk cache can be up to an hour stale — a coach added to a room since
      // then won't be in it yet. Force one fresh fetch per missing id rather
      // than waiting out the full cache window.
      if (!cached && !missingCoachRetried.has(userId)) {
        missingCoachRetried.add(userId);
        refreshCoaches(get);
      }
      return cached;
    },
    [get]
  );

  return { getClientName, getCoachName, isReady: !!memoryCache };
}
